import type { FirewallReplayRecord } from '@nadoutong/contracts';

/**
 * What the console can say about the replay gate BEFORE it calls activate
 * (#1760, gate landed in #1759).
 *
 * The gate itself is server-side and reads storage inside the activation
 * transaction: a version may be armed only when a replay record exists for the
 * exact chain about to go live, measured against the version armed right now.
 * Two of its inputs are invisible from here — the resolved chain hash (a PARENT
 * activation moves a child's hash without touching the child) and any replay
 * another operator ran. So this is deliberately ADVISORY: it decides what to
 * warn about, never what to forbid.
 *
 * Forbidding is the dangerous direction. `version: null` and re-arming the
 * armed version are ungated precisely because they are what an incident needs,
 * and a client-side model that guessed wrong would take the rollback path away
 * at the worst possible moment. The activate button stays live in every state;
 * a refusal comes back from the server with its own explanation.
 */

export type FirewallActivationEvidence =
  /** The gate does not apply to this activation at all. */
  | { readonly kind: 'ungated'; readonly reason: 'disarm' | 'rearm' }
  /** A record from this session covers this version against the armed baseline. */
  | { readonly kind: 'ready'; readonly record: FirewallReplayRecord }
  /** A record exists, but the baseline moved under it — the server will refuse. */
  | { readonly kind: 'stale'; readonly record: FirewallReplayRecord }
  /** Nothing this session knows about covers this activation. */
  | { readonly kind: 'missing' };

export function firewallActivationEvidence({
  targetVersion,
  activeVersion,
  record,
}: {
  readonly targetVersion: number | null;
  readonly activeVersion: number | null;
  /** The newest replay this session ran, whatever it was for. */
  readonly record: FirewallReplayRecord | null;
}): FirewallActivationEvidence {
  if (targetVersion === null) return { kind: 'ungated', reason: 'disarm' };
  if (activeVersion !== null && targetVersion === activeVersion) {
    return { kind: 'ungated', reason: 'rearm' };
  }
  if (record === null || record.version !== targetVersion) return { kind: 'missing' };
  // `null === null` is the case that matters: a replay run while nothing was
  // armed is valid evidence only while nothing is armed. Disarming invalidates
  // evidence taken against an armed baseline, and vice versa.
  return record.baseline_version === activeVersion
    ? { kind: 'ready', record }
    : { kind: 'stale', record };
}
